import { CAPABILITIES } from '@/config/platform';
import { DeepKeys, FormApi, FormOptions, useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import i18next from 'i18next';

export type FormType<T> = FormOptions<T, ReturnType<typeof zodValidator>>;

export type OnSubmit<T> = FormType<T>['onSubmit'];

export function useTypedForm<T>(
  opts: FormOptions<T, ReturnType<typeof zodValidator>>,
): ReturnType<typeof useForm<T, ReturnType<typeof zodValidator>>> {
  return useForm<T, ReturnType<typeof zodValidator>>({
    ...opts,
    validatorAdapter: zodValidator(),
  });
}

export function setFieldError<T>(
  formApi: FormApi<T, ReturnType<typeof zodValidator>>,
  field: DeepKeys<T>,
  ...errors: string[]
) {
  formApi.setFieldMeta(field, (meta) => {
    meta.errors = errors;
    return meta;
  });
}

export const REFRESH_TOKEN = 'refresh_token';

export const ACCESS_TOKEN = 'access_token';

/** Session ouverte depuis la démo du site vitrine (lecture seule). */
export const DEMO_MODE = 'demo_mode';

/** Émis quand le serveur refuse une écriture en mode démo. */
export const DEMO_BLOCKED_EVENT = 'kara:demo-blocked';
/**
 * Cookie options for access token
 */

export function isSSR() {
  return typeof window === 'undefined';
}

export function isMac() {
  if (typeof navigator === 'undefined') return false;
  return navigator?.userAgent.includes('Macintosh');
}

export const getClientSideCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const cookieValue = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];

  return cookieValue;
};

export async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/// Dans l'app des magasins, les pages légales s'ouvrent sans l'en-tête ni le
/// pied du site (`?app=1`) : leurs liens « Tarifs » mèneraient à un paiement
/// hors de l'achat intégré (règle 3.1.1). Leurs CGU sont celles du site, qui
/// portent les règles des contenus publiés qu'exige la règle 1.2 ; le web et
/// le bureau gardent celles de GitBook. Le site répond sur `www` : depuis son
/// passage sur la VM, l'adresse nue n'a pas de certificat à son nom.
const LEGAL_PAGE_QUERY = CAPABILITIES.linkToExternalPurchase ? '' : '?app=1';
export const TNC_URL = CAPABILITIES.linkToExternalPurchase
  ? 'https://kara-trading.gitbook.io/kara-trading-docs/7dbJTpo2bXPVDklbOCsF/legal/cgu-and-rgpd'
  : `https://www.kara-trading-tools.com/legal/terms${LEGAL_PAGE_QUERY}`;
export const PRIVACY_POLICY_URL = `https://www.kara-trading-tools.com/privacy${LEGAL_PAGE_QUERY}`;

function formatFileSize(size: number): string {
  if (Number.isInteger(size)) return `${size}`;
  return `${size.toFixed(1)}`;
}

export function fileSizeToReadable(size: number): string {
  if (size < 1_000) return `${size}${i18next.t('base:byteLetter')}`;
  if (size < 1_000_000)
    return `${formatFileSize(size / 1_000)}K${i18next.t('base:byteLetter')}`;
  if (size < 1_000_000_000)
    return `${formatFileSize(size / 1_000_000)}M${i18next.t('base:byteLetter')}`;
  if (size < 1_000_000_000_000)
    return `${formatFileSize(size / 1_000_000_000)}G${i18next.t('base:byteLetter')}`;
  if (size < 1_000_000_000_000_000)
    return `${formatFileSize(size / 1_000_000_000_000)}T${i18next.t('base:byteLetter')}`;
  return `${size}${i18next.t('base:byteLetter')}`;
}

const formatterDigit = new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

export function formatWithCurrency(amount: number, currency = 'USD') {
  const amountStr = formatterDigit.format(amount);

  switch (currency.toUpperCase()) {
    case 'USD':
      return `${amountStr}$`;
    case 'EUR':
      return `${amountStr}€`;
    case 'GBP':
      return `${amountStr}£`;
    default:
      return `${amountStr}${currency}`;
  }
}

export default function floatEqual(a: number, b: number) {
  if (a === b) {
    return true;
  }

  const diff = Math.abs(a - b);
  if (diff < Number.EPSILON) {
    return true;
  }
  return diff <= Number.EPSILON * Math.min(Math.abs(a), Math.abs(b));
}

export function excludeNan(value: number, defaultValue = 0) {
  if (isNaN(value)) return defaultValue;
  return value;
}

/** Le message du backend quand il y en a un, sinon celui de l'erreur. */
export function apiMessage(e: unknown): string {
  const r = (e as { response?: { data?: { message?: string | string[] } } })
    .response;
  const m = r?.data?.message;
  if (Array.isArray(m)) return m.join(' ');
  return m ?? (e as Error).message;
}
